import { Button, FlexBox, Icon, IconButton, Popover, PopoverContent, PopoverLauncher, Typography, Visible, useSwanPopover } from '@vp/swan'
import type { OverlayTriggerState } from '@react-stately/overlays'
import { MyAccountResourcesContentProvider } from '@vp/my-account-pages-dropdown'
import LinkWithIcon from '../header-links/LinkWithIcon'
import useClient from '../../../hooks/useClient'
import { useI18n } from '../../../context/TranslationContext'
import { MyAccountGuestPop } from './GuestPopUp'
import { MyAccountUserPop } from './UserPopUp'
import { useAuth } from '../../../hooks/useAuth'
import type { HeaderLink } from '../../../types/site-header'
import { useLayoutEffect } from 'react'
import { useAccountPopover } from '../../../hooks/useAccountPopover'

const siteHeaderAccountMFEContainerId = 'siteHeaderAccountMFEContainer'

type MyAccountProps = {
  accountLink: HeaderLink
  isRedesignedSignInButtonEnabled: boolean
  isLoggedInSsr: boolean
}

export const MyAccount: React.FC<MyAccountProps> = ({ accountLink, isRedesignedSignInButtonEnabled, isLoggedInSsr }) => {
  const isClient = useClient()

  if (!isClient)
    return <SSRMyAccount accountLink={accountLink} isRedesignedSignInButtonEnabled={isRedesignedSignInButtonEnabled} isLoggedInSsr={isLoggedInSsr} />
  return (
    <MyAccountResourcesContentProvider>
      <CSRMyAccount accountLink={accountLink} isRedesignedSignInButtonEnabled={isRedesignedSignInButtonEnabled} isLoggedInSsr={isLoggedInSsr} />
    </MyAccountResourcesContentProvider>
  )
}

type SSRMyAccountProps = {
  accountLink: HeaderLink
  isRedesignedSignInButtonEnabled: boolean
  isLoggedInSsr: boolean
}

const SSRMyAccount: React.FC<SSRMyAccountProps> = ({ accountLink, isRedesignedSignInButtonEnabled, isLoggedInSsr }) => {
  const { translate } = useI18n()
  const accountText = translate(isLoggedInSsr ? 'myAccount' : 'signIn')

  if (isRedesignedSignInButtonEnabled && !isLoggedInSsr) return <ButtonWithIcon accountText={accountText} />

  return <LinkWithIcon id={siteHeaderAccountMFEContainerId} href={accountLink.url} iconType="account" label={accountText} analyticsId="account" />
}

const CSRMyAccount: React.FC<SSRMyAccountProps> = ({ accountLink, isRedesignedSignInButtonEnabled, isLoggedInSsr }) => {
  const { isSignedIn: isSignedInClient, signIn } = useAuth()
  const isSignedIn = isSignedInClient ?? isLoggedInSsr
  const { translate } = useI18n()
  const accountText = translate(isSignedIn ? 'myAccount' : 'signIn')

  if (isRedesignedSignInButtonEnabled && !isSignedIn) {
    return <ButtonWithIcon onClick={signIn} accountText={accountText} />
  }

  return (
    <>
      {isSignedIn && (
        <Visible xs>
          <LinkWithIcon
            id={siteHeaderAccountMFEContainerId}
            href={accountLink.url}
            iconType="account"
            label={accountText}
            analyticsId={accountLink.analyticsId || 'account'}
          />
        </Visible>
      )}
      <Visible sm md lg xl>
        <FlexBox justifyContent="center" alignItems="center">
          <AccountPopover isSignedIn={isSignedIn} accountText={accountText} />
        </FlexBox>
      </Visible>
    </>
  )
}

type ButtonWithIconProps = {
  onClick?: () => void
  accountText: string
}

const ButtonWithIcon = ({ onClick, accountText }: ButtonWithIconProps) => {
  return (
    <>
      <Visible xs sm md>
        <IconButton
          tooltip={accountText}
          onClick={onClick}
          onKeyDown={(e) => ['Enter', ' '].includes(e.key) && onClick?.()}
          skin="primary"
          ml={{ sm: 'to-actions' }}
        >
          <Icon iconType="account" skin="standard" aria-hidden="true" />
        </IconButton>
      </Visible>
      <Visible xl lg>
        <Button
          ml="to-actions"
          skin="primary"
          onClick={onClick}
          iconPosition="left"
          onKeyDown={(e) => ['Enter', ' '].includes(e.key) && onClick?.()}
          aria-label={accountText}
        >
          <Icon iconType="account" skin="standard" aria-hidden="true" />
          <Visible xl lg>
            <Typography fontSkin="body-small-bold">{accountText}</Typography>
          </Visible>
        </Button>
      </Visible>
    </>
  )
}

/** Captures SWAN Popover's internal OverlayTriggerState into a parent ref. */
const OverlayStateCapture: React.FC<{
  stateRef: React.RefObject<OverlayTriggerState | null>
}> = ({ stateRef }) => {
  const { overlayState } = useSwanPopover()
  useLayoutEffect(() => {
    stateRef.current = overlayState
  })
  return null
}

type AccountPopoverProps = {
  isSignedIn: boolean
  accountText: string
}

const AccountPopover: React.FC<AccountPopoverProps> = ({ isSignedIn, accountText }) => {
  const { profile } = useAuth()
  const {
    accountRef,
    overlayStateRef,
    handleTriggerFocusCapture,
    handlePointerDownCapture,
    handleContentMouseEnter,
    handleContentMouseLeave,
    handleContentMouseDown,
    handleAccountClick,
  } = useAccountPopover({ isSignedIn })

  return (
    <div ref={accountRef} onFocusCapture={handleTriggerFocusCapture} onPointerDownCapture={handlePointerDownCapture}>
      <Popover position="bottom">
        <OverlayStateCapture stateRef={overlayStateRef} />
        <PopoverLauncher>
          <Button skin="unstyled" fontSkin="body-small" onClick={handleAccountClick} aria-label={accountText} ml={{ sm: 'to-actions' }} p={{ xs: 4, lg: 0 }}>
            <FlexBox gap="between-icon-and-text" alignItems="center">
              <Icon iconType="account" size="standard" aria-hidden="true" />
              <Visible xl lg>
                <Typography>{accountText}</Typography>
              </Visible>
            </FlexBox>
          </Button>
        </PopoverLauncher>
        <PopoverContent
          isNonModal
          // Setting undefined as fallback - without it the attribute shows eslint error which is weird,
          // since this does no harm as accountText is always going to resolve a string value, but this is to satisfy the linter
          aria-label={accountText || undefined}
          data-state={isSignedIn ? 'signed-in' : 'signed-out'}
          px={isSignedIn ? 5 : 0}
          pt={isSignedIn ? 5 : 0}
          onMouseDown={handleContentMouseDown}
          onMouseEnter={handleContentMouseEnter}
          onMouseLeave={handleContentMouseLeave}
        >
          {!isSignedIn && <MyAccountGuestPop />}
          {isSignedIn && <MyAccountUserPop profile={profile} />}
        </PopoverContent>
      </Popover>
    </div>
  )
}
